import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CtyButton, CtyShowToast, checkType } from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { useSelector } from 'react-redux';
import { useGetFgaUserResourcesQuery, RootState } from '@circuitry-ai/doc-data';

interface PageHeaderProps {
    heading: string,
    description?: string,
    pageHeaderClass?: string;
    actionButton?: React.ReactNode;
    showCreateButton?: boolean;
    createButtonText?: string;
    onCreateButtonClick?: () => void;
    entityType?: string; // advisor, agent, analyst, specialist
}

const PageHeader = (props: PageHeaderProps) => {

    const { t } = useTranslation();
    const { heading, description = '', pageHeaderClass = '', actionButton, showCreateButton = false, createButtonText = '', onCreateButtonClick, entityType } = props;
    const [isCreateLoading, setIsCreateLoading] = useState(false);

    const { details: user } = useSelector((state: RootState) => state.auth.user);
    const userId = user?.sub;
    const { refetch } = useGetFgaUserResourcesQuery(userId || '', {
        skip: !userId || !entityType,
    });

    const handleCreateClick = async () => {
        if (!entityType) {
            onCreateButtonClick?.();
            return;
        }

        setIsCreateLoading(true);
        try {
            // Refetch to get latest permissions on button click
            const result = await refetch();
            const resourcesData = result.data?.data?.resources;

            if (!resourcesData?.entity_types) {
                onCreateButtonClick?.();
                return;
            }

            const entityTypes = resourcesData.entity_types;
            const matchedEntity = entityTypes.find(
                (entity: any) =>
                    entity.id === entityType &&
                    entity.relation === 'create_instance'
            );

            if (!matchedEntity) {
                const entityName = entityType.charAt(0).toUpperCase() + entityType.slice(1);
                CtyShowToast(checkType.error, t(`You do not have access to create ${entityName}.`));
                return;
            }

            onCreateButtonClick?.();
        } finally {
            setIsCreateLoading(false);
        }
    };

    return (
        <div className={`flex flex-col gap-3 px-6 py-4 ${pageHeaderClass}`}>
            <div className='flex flex-wrap items-center justify-between gap-2'>
                <div className="text-foreground font-sfui font-semibold text-3xl leading-[38px]">{t(heading)}</div>
                {
                    showCreateButton ? <CtyButton
                        id="create-connection-btn"
                        color="primary"
                        variant="bordered"
                        className={`font-bold ${isCreateLoading ? 'cursor-wait' : ''}`}
                        size="sm"
                        onPress={handleCreateClick}
                        isLoading={isCreateLoading}
                        isDisabled={isCreateLoading}
                        startContent={!isCreateLoading ? <Icon icon="fa6-solid:plus" width="15" height="15" style={{ color: 'primary' }} /> : undefined}
                    >
                        {createButtonText}
                    </CtyButton> : actionButton
                }
            </div>
            <p className="font-poppins text-sm font-normal leading-5 dark:text-[#8a8a8a]">{description}</p>
        </div>
    );
};

export { PageHeader };